import { Token } from "../utils/localStorage.util";
import axios, {
    AxiosError,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from "axios";
import config from "../config.js";
import {
    buildBuscarConciliacoesPath,
    buildFiltrarExtratosPorTextoPath,
    buildFiltrarIntervaloPath,
} from "../utils/apiConciliacaoPaths.util";

const apiConciliacao = axios.create({
    baseURL: config.REACT_APP_HOST_API_CONCILIACAO,
});

apiConciliacao.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const token = Token.getToken();
        if (token) {
            config.headers.Authorization = token;
        }

        if (!config.headers["Content-Type"]) {
            config.headers["Content-Type"] = "application/json";
        }

        return config;
    },
    (error: AxiosError) => {
        Promise.reject(error);
    }
);

apiConciliacao.interceptors.response.use(
    (response: AxiosResponse) => {
        return response.data;
    },

    async function (error: AxiosError) {
        if (error?.code === "ERR_NETWORK") {
            return;
        }

        return Promise.reject(error);
    }
);


// ----------------------------------------------------------------------------------------------------

//Contas Bancária

// API Conciliação - Buscar Contas Bancárias
export async function buscarContasBancarias(
    empresa: number
){
    const retorno = await apiConciliacao.get(
        `/contas-bancarias/${empresa}`
    );
    
    return retorno
}

// API Conciliação - Buscar Contas Bancárias por Texto
export function buscarContasBancariasPorTexto(
    empresa: number,
    texto: string
){
    return apiConciliacao.get(
        `/contas-bancarias/${empresa}/buscar-conta?textoDeBusca=${encodeURIComponent(texto)}`
    );
}

//API Conciliação - Inserir
export function cadastrarContaBancaria(
    empresa: string,
    body: any
) {
    return apiConciliacao.post(`/contas-bancarias/${empresa}`, body);
}

//API Conciliação - Atualizar
export function atualizarContaBancaria(
    empresa: string,
    id_conciliacao_conta_bancaria: number,
    body: any
) {
    return apiConciliacao.put(
        `/contas-bancarias/${empresa}/${id_conciliacao_conta_bancaria}`,
        body);
}

//API Conciliação - Deletar
export function deletarContaBancaria(
    empresa: string,
    id_conciliacao_conta_bancaria: number
) {
    return apiConciliacao.delete(
        `/contas-bancarias/${empresa}/${id_conciliacao_conta_bancaria}`);
}

// ----------------------------------------------------------------------------------------------------

//Contas Domínio

// API Conciliação - Buscar Contas Domínio
export function buscarContasDominio(){
    return apiConciliacao.get(
        `/contas-dominio`
    );
}

// API Conciliação - Buscar Contas Domínio por Texto
export function buscarContasDominioPorTexto(
    texto: string
){
    return apiConciliacao.get(
        `/contas-dominio/buscar-contas?textoDeBusca=${encodeURIComponent(texto)}`
    );
}

//API Conciliação - Inserir Conta Domínio
export function cadastrarContaDominio(
    body: any
) {
    return apiConciliacao.post(`/contas-dominio`, body);
}

//API Conciliação - Atualizar Conta Domínio
export function atualizarContaDominio(
    id_conciliacao_conta_contabil: number,
    body: any
) {
    return apiConciliacao.put(
        `/contas-dominio/${id_conciliacao_conta_contabil}`,
        body);
}

//API Conciliação - Deletar Conta Domínio
export function deletarContaDominio(
    id_conciliacao_conta_contabil: number
) {
    return apiConciliacao.delete(
        `/contas-dominio/${id_conciliacao_conta_contabil}`);
}

export function buscarContaDominioFiltros(
    id_conciliacao_conta_contabil: number
) {
    return apiConciliacao.get(
        `/contas-dominio/${id_conciliacao_conta_contabil}/filtros`);
}

export function buscarContaDominioFiltrosDisponiveis(
    id_conciliacao_conta_contabil: number
) {
    return apiConciliacao.get(
        `/contas-dominio/${id_conciliacao_conta_contabil}/filtros-disponiveis`);
}
// ----------------------------------------------------------------------------------------------------

//Contas Protheus

// API Conciliação - Buscar Contas Protheus
export function buscarContasProtheus(){
    return apiConciliacao.get(
        `/contas-protheus`
    );
}

// API Conciliação - Buscar Contas Protheus por Texto
export function buscarContasProtheusPorTexto(
    texto: string
){
    return apiConciliacao.get(
        `/contas-protheus/buscar-contas?textoDeBusca=${encodeURIComponent(texto)}`
    );
}

//API Conciliação - Inserir Conta Protheus
export function cadastrarContaProtheus(
    body: any
) {
    return apiConciliacao.post(`/contas-protheus`, body);
}

//API Conciliação - Atualizar Conta Protheus
export function atualizarContaProtheus(
    id_conciliacao_conta_contabil: number,
    body: any
) {
    return apiConciliacao.put(
        `/contas-protheus/${id_conciliacao_conta_contabil}`,
        body);
}

//API Conciliação - Deletar Conta Protheus
export function deletarContaProtheus(
    id_conciliacao_conta_contabil: number
) {
    return apiConciliacao.delete(
        `/contas-protheus/${id_conciliacao_conta_contabil}`);
}

export function buscarContaProtheusFiltros(
    id_conciliacao_conta_contabil: number
) {
    return apiConciliacao.get(
        `/contas-protheus/${id_conciliacao_conta_contabil}/filtros`);
}

export function buscarContaProtheusFiltrosDisponiveis(
    id_conciliacao_conta_contabil: number
) {
    return apiConciliacao.get(
        `/contas-protheus/${id_conciliacao_conta_contabil}/filtros-disponiveis`);
}
// ----------------------------------------------------------------------------------------------------

//Banco - Filtros

// Cadastro de bancos (COMPE) para select em vínculos
export function buscarBancosBrasileiros() {
    return apiConciliacao.get(`/filtros/bancos-brasileiros`);
}

// API Conciliação - Buscar Banco Filtros
export function buscarBancoFiltros(){
    return apiConciliacao.get(
        `/filtros/vinculo-contas`
    );
}

// API Conciliação - Buscar Banco Filtros por Texto
export function buscarBancoFiltrosPorTexto(
    texto: string
){
    return apiConciliacao.get(
        `/filtros/vinculo-contas/buscar-filtro?textoDeBusca=${encodeURIComponent(texto)}`
    );
}

//API Conciliação - Inserir
export function cadastrarBancoFiltro(
    body: any
) {
    return apiConciliacao.post(`/filtros/vinculo-contas`, body);
}

//API Conciliação - Atualizar
export function atualizarBancoFiltro(
    id_conciliacao_conta_contabil: number,
    body: any
) {
    return apiConciliacao.put(
        `/filtros/vinculo-contas/${id_conciliacao_conta_contabil}`,
        body);
}

//API Conciliação - Deletar
export function deletarBancoFiltro(
    id_conciliacao_conta_contabil: number
) {
    return apiConciliacao.delete(
        `/filtros/vinculo-contas/${id_conciliacao_conta_contabil}`);
}

// ----------------------------------------------------------------------------------------------------

//Código Fornecedor

// API Conciliação - Buscar Código do Fornecedor
export function buscarCodFornecedor(){
    return apiConciliacao.get(
        `/filtros/codigo-fornecedor`
    );
}

// API Conciliação - Buscar Código do Fornecedor por Texto
export function buscarCodFornecedorPorTexto(
    texto: string
){
    return apiConciliacao.get(
        `/filtros/codigo-fornecedor/buscar-filtro?textoDeBusca=${encodeURIComponent(texto)}`
    );
}

//API Conciliação - Inserir
export function cadastrarCodFornecedor(
    body: any
) {
    return apiConciliacao.post(`/filtros/codigo-fornecedor`, body);
}

//API Conciliação - Atualizar
export function atualizarCodFornecedor(
    id_conciliacao_filtro_codigo_fornecedor: number,
    body: any
) {
    return apiConciliacao.put(
        `/filtros/codigo-fornecedor/${id_conciliacao_filtro_codigo_fornecedor}`,
        body);
}

//API Conciliação - Deletar
export function deletarCodFornecedor(
    id_conciliacao_filtro_codigo_fornecedor: number
) {
    return apiConciliacao.delete(
        `/filtros/codigo-fornecedor/${id_conciliacao_filtro_codigo_fornecedor}`);
}

// ----------------------------------------------------------------------------------------------------

//Histórico de Extrato

// API Conciliação - Buscar Código do Fornecedor
export function buscarHistoricoExtrato(){
    return apiConciliacao.get(
        `/filtros/historico-extrato`
    );
}

// API Conciliação - Buscar Código do Fornecedor por Texto
export function buscarHistoricoExtratoPorTexto(
    texto: string
){
    return apiConciliacao.get(
        `/filtros/historico-extrato/buscar-filtro?textoDeBusca=${encodeURIComponent(texto)}`
    );
}

//API Conciliação - Inserir
export function cadastrarHistoricoExtrato(
    body: any
) {
    return apiConciliacao.post(`/filtros/historico-extrato`, body);
}

//API Conciliação - Atualizar
export function atualizarHistoricoExtrato(
    id_conciliacao_filtro_historico_extrato: number,
    body: any
) {
    return apiConciliacao.put(
        `/filtros/historico-extrato/${id_conciliacao_filtro_historico_extrato}`,
        body);
}

//API Conciliação - Deletar
export function deletarHistoricoExtrato(
    id_conciliacao_filtro_historico_extrato: number
) {
    return apiConciliacao.delete(
        `/filtros/historico-extrato/${id_conciliacao_filtro_historico_extrato}`);
}

// ----------------------------------------------------------------------------------------------------

//Extrato

//Buscar Extratos
export function buscarExtrato(idEmpresa: number){
    return apiConciliacao.get(`/extratos/${idEmpresa}`);
}

//Buscar Extratos
export function buscarExtratoPorTexto(idEmpresa: number, textoDeBusca: string){
    return apiConciliacao.get(buildFiltrarExtratosPorTextoPath(idEmpresa, textoDeBusca));
}

//Excluir Extrato
export function excluirExtrato(idEmpresa: number, idExtrato: number){
    return apiConciliacao.delete(`/extratos/${idEmpresa}/extrato/${idExtrato}`);
}

//Buscar Transacoes
export function buscarTransacoes(idEmpresa: number, idExtrato: number){
    return apiConciliacao.get(`/extratos/${idEmpresa}/transacoes/${idExtrato}`);
}

export type BuscarConciliacoesParams = {
    tipoContabil?: "dominio" | "protheus";
    pagina?: number;
    tamanhoPagina?: number;
    /** omitido ou "todos" = sem filtro por coluna ignorar */
    filtroIgnorado?: "sim" | "nao" | "todos";
    descricao?: string;
    /** Decimal com ponto e 2 casas (ex.: "1234.56") para igualdade exata com a coluna quantia */
    quantiaBusca?: string;
    contaDebito?: string;
    descricaoDebito?: string;
    contaCredito?: string;
    descricaoCredito?: string;
    /** Data inicial (YYYY-MM-DD), filtra dt_transacao >= início do dia America/Sao_Paulo */
    dtInicio?: string;
    /** Data final (YYYY-MM-DD), filtra dt_transacao <= fim do dia America/Sao_Paulo */
    dtFim?: string;
};

//Buscar Conciliacoes
export function buscarConciliacoes(
    idEmpresa: number,
    idExtrato: number,
    params?: BuscarConciliacoesParams
) {
    return apiConciliacao.get(
        buildBuscarConciliacoesPath(idEmpresa, idExtrato, params)
    );
}

//Atualizar Conciliacoes
export function atualizarConciliacoes(idEmpresa: number, idExtrato: number, idConciliacao: number, body: any){
    return apiConciliacao.put(`/extratos/${idEmpresa}/conciliacao/${idExtrato}/atualizar/${idConciliacao}`, body);
}

//Exportar Conciliacoes Dominio
export function exportarConciliacoesDominio(idEmpresa: number, idExtrato: number){
    return apiConciliacao.get(`/extratos/${idEmpresa}/conciliacao/${idExtrato}/exportacao-dominio`);
}

//Exportar Conciliacoes Protheus
export function exportarConciliacoesProtheus(idEmpresa: number, idExtrato: number){
    return apiConciliacao.get(`/extratos/${idEmpresa}/conciliacao/${idExtrato}/exportacao-protheus`);
}

// Filtrar / limpar intervalo de exportação (body.intervalo vazio remove o recorte)
export function filtrarConciliacoesPorData(idEmpresa: number, idExtrato: number, intervalo: any){
    return apiConciliacao.put(
        buildFiltrarIntervaloPath(idEmpresa, idExtrato),
        intervalo
    );
}

//Restaurar Conciliacoes
export function restaurarConciliacoes(idEmpresa: number, idExtrato: number){
    return apiConciliacao.put(`/extratos/${idEmpresa}/conciliacao/${idExtrato}/restaurar`);
}

//Restaurar Conciliacoes
export function buscarConciacaoContabil(grupo: number, refFormatada: string){
    return apiConciliacao.get(`/conciliacao-contabil?grupo=${grupo}&referencia=${refFormatada}`);
}

export default apiConciliacao;

// ----------------------------------------------------------------------------------------------------
// Importações assíncronas (fila) — leitura via conciliação; upload permanece na api-openai

export function listarTodasImportacoesExtrato(limit = 100) {
  return apiConciliacao.get(`/importacoes`, { params: { limit } });
}

export function pollingTodasImportacoesExtrato() {
  return apiConciliacao.get(`/importacoes/polling`);
}

export function listarImportacoesExtrato(idEmpresa: number | string, limit = 100) {
  return apiConciliacao.get(`/importacoes/${idEmpresa}`, { params: { limit } });
}

export function pollingImportacoesExtrato(idEmpresa: number | string) {
  return apiConciliacao.get(`/importacoes/${idEmpresa}/polling`);
}

export function buscarImportacaoExtrato(idEmpresa: number | string, idJob: number) {
  return apiConciliacao.get(`/importacoes/${idEmpresa}/${idJob}`);
}

export function buscarLogsImportacaoExtrato(idEmpresa: number | string, idJob: number) {
  return apiConciliacao.get(`/importacoes/${idEmpresa}/${idJob}/logs`);
}